import { useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { LoadingSpinner } from '../../components/Molecules';
import { GetCustomBreadPopup } from '../../components/Popups/GetCustomBreadPopup';
import { GetMoreBreadTemplate } from '../../components/Templates';
import { AppToastErrorOption } from '../../constants/toast';
import { usePopup } from '../../context';
import useCancelRedemption from '../../hooks/useCancelRedemption';
import useLoadRedemption from '../../hooks/useLoadRedemption';
import useRedirectEntry from '../../hooks/useRedirectEntry';
import { useRewardStore } from '../../stores/RewardStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useUserStore } from '../../stores/UserStore';

const { createRedemption } = useRewardStore.getState();

const GetMoreBreadScreen = () => {
  const { t } = useTranslation();

  useRedirectEntry({ allowTrial: false });

  const activeUserId = useSessionStore((store) => store.activeUserId);
  const profile = useUserStore((state) => state.getProfile(activeUserId ?? ''));
  const rewardOfferStore = 'system';
  const rewardOfferId = 'bread';

  const [isLoading, setIsLoading] = useState(false);
  const [rewardRedemptionId, setRewardRedemptionId] = useState<
    string | undefined
  >();

  const cancelRedemption = useCancelRedemption(rewardOfferStore, rewardOfferId);

  const onClickPurchase = useCallback(
    (bread: number) => {
      setIsLoading(true);

      createRedemption(rewardOfferStore, {
        rewardOfferId,
        quantity: bread / 100,
        paymentGateway: profile?.localCurrency === 'CNY' ? 'stripe' : 'paypal',
        currency: profile?.localCurrency,
      })
        .then((res) => {
          if (res) {
            setRewardRedemptionId(res.rewardRedemptionId);
          } else {
            setIsLoading(false);
            toast(
              t(
                'Failed to create order. Please refresh the page and try again.',
              ),
              AppToastErrorOption,
            );
            cancelRedemption();
          }
        })
        .catch(() => {
          setIsLoading(false);
          toast(
            t('Failed to create order. Please refresh the page and try again.'),
            AppToastErrorOption,
          );
          cancelRedemption();
        });
    },
    [cancelRedemption, profile?.localCurrency, t],
  );

  useLoadRedemption(rewardOfferStore, rewardRedemptionId, setIsLoading);

  const { showPopup, hidePopup } = usePopup();

  const showGetCustomBreadPopup = useCallback(() => {
    showPopup(
      <GetCustomBreadPopup
        currency={profile?.localCurrency}
        onPrimary={(bread) => {
          hidePopup();
          onClickPurchase(bread);
        }}
        onCancel={hidePopup}
      />,
    );
  }, [hidePopup, onClickPurchase, profile?.localCurrency, showPopup]);

  return (
    <>
      {isLoading && <LoadingSpinner loading={isLoading} />}
      <GetMoreBreadTemplate
        currency={profile?.localCurrency}
        onClickPurchase={(bread) => {
          if (bread === undefined) {
            showGetCustomBreadPopup();
          } else {
            onClickPurchase(bread);
          }
        }}
      />
    </>
  );
};

export default GetMoreBreadScreen;
